import type { ReactNode } from 'react';

import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';

import { AnimatedStep } from './animated-step';
import { GuidedPlanningFooter } from './guided-planning-footer';
import { GuidedPlanningLoadingOverlay } from './guided-planning-loading-overlay';

interface StepLayoutProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  isNextDisabled?: boolean;
  nextButtonLabel?: string;
  backButtonLabel?: string;
  lastStepButtonLabel?: string;
  nextButtonIcon?: ReactNode;
  backButtonIcon?: ReactNode;
  nextButtonVariant?: 'default' | 'outline' | 'ghost';
  /** Custom next handler (for substeps) */
  onNext?: () => void;
  /** Custom back handler (for substeps) */
  onBack?: () => void;
  /** Hide back button */
  hideBack?: boolean;
  /** Show loading spinner - combined with session loading state */
  isLoading?: boolean;
  /** Numeric key for substep animations. When provided, enables animated transitions between substeps. */
  animationKey?: number;
}

export function StepLayout({
  title,
  subtitle,
  icon,
  children,
  footer,
  isNextDisabled,
  nextButtonLabel,
  backButtonLabel,
  lastStepButtonLabel,
  nextButtonIcon,
  backButtonIcon,
  nextButtonVariant,
  onNext,
  onBack,
  hideBack,
  isLoading: isLoadingProp,
  animationKey,
}: StepLayoutProps) {
  const { isLoading: isSessionLoading } = useSession();
  const { isSaving } = useSessionMutations();

  // Always show loading overlay when session is loading or saving
  const isLoading = isSessionLoading || isSaving || (isLoadingProp ?? false);

  const content = (
    <div className="relative mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-12">
      <GuidedPlanningLoadingOverlay isLoading={isLoading} />

      <div className="mx-auto mb-6 max-w-3xl text-center sm:mb-8">
        {icon && <div className="mb-4 flex justify-center sm:mb-6">{icon}</div>}
        <h1 className="text-balance text-2xl font-bold text-gray-950 sm:text-3xl">{title}</h1>
        {subtitle && (
          <p className="mt-2 text-balance text-base text-gray-700 sm:mt-3 sm:text-lg">{subtitle}</p>
        )}
      </div>

      {children}

      {footer ?? (
        <GuidedPlanningFooter
          isNextDisabled={isNextDisabled}
          nextButtonLabel={nextButtonLabel}
          backButtonLabel={backButtonLabel}
          lastStepButtonLabel={lastStepButtonLabel}
          nextButtonIcon={nextButtonIcon}
          backButtonIcon={backButtonIcon}
          nextButtonVariant={nextButtonVariant}
          onNext={onNext}
          onBack={onBack}
          hideBack={hideBack}
          isLoading={isLoading}
        />
      )}
    </div>
  );

  if (animationKey !== undefined) {
    return <AnimatedStep stepKey={animationKey}>{content}</AnimatedStep>;
  }

  return content;
}
